import React, { useCallback, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { connect } from 'react-redux';

import Dropdown from 'common/components/Dropdown';
import { TheaterReservationTracking } from 'common/constants/trackingEventsNames';
import formatDate from 'common/tools/date/format';
import {
  addDay,
  isSameDay,
  resetTimeForDate
} from 'common/tools/date/manipulate';
import { formatDayString } from 'common/tools/date/showtime';
import capitalize from 'common/tools/string/capitalize';
import trans from 'common/tools/translations/trans';

import TheaterCard from 'website/components/TheaterCard';
import ShowtimesVersionHolder from 'website/components/showtimes/ShowtimesVersionHolder';
import {
  MovieFromAttributes,
  RequestFavoriteData
} from 'website/containers/showtimes/types';
import { RequestPayload } from 'website/containers/showtimes/utils/types';
import { waitForLoginStatus } from 'website/containers/user/connection';
import { Dispatch, State } from 'website/reducers';
import { openShowtimeModal } from 'website/services/modal';

import { buildShowtimesGroups, requestShowtimes } from '../utils';

type FavoriteTheater = {
  id: string;
  label: string;
};

type DateOption = {
  label: string;
  value: Date;
};

type FavoriteTheaterData = {
  theater: any | null;
  showtimes: Record<string, any[]> | null;
  message?: string | null;
};

type FavoriteCinemasShowtimeUiProps = {
  dispatch: Dispatch;
  movie: MovieFromAttributes;
  user?: any;
  favoriteTheaters: FavoriteTheater[];
};

const DAY_FORMAT = {
  weekday: 'long',
  day: '2-digit',
  month: 'long'
} as Intl.DateTimeFormatOptions;

// selectable range: today + the next 7 days, like the userspace date filter
const DAYS_AHEAD = 7;

const getToday = () => {
  const today = new Date();
  today.setSeconds(0);
  today.setMilliseconds(0);
  return today;
};

const hasAnyShowtime = (showtimes?: Record<string, any[]> | null) =>
  !!showtimes &&
  Object.values(showtimes).some(
    hours => Array.isArray(hours) && hours.length > 0
  );

const FavoriteCinemasShowtimeUi = ({
  dispatch,
  movie,
  user,
  favoriteTheaters = []
}: FavoriteCinemasShowtimeUiProps) => {
  const [selectedTheaterId, setSelectedTheaterId] = useState<string | null>(
    favoriteTheaters[0]?.id || null
  );
  const [selectedDate, setSelectedDate] = useState<Date>(getToday);
  const [theaterDataMap, setTheaterDataMap] = useState<
    Record<string, FavoriteTheaterData>
  >({});
  const [loading, setLoading] = useState(false);

  const dateOptions = useMemo<DateOption[]>(() => {
    const today = getToday();
    const options: DateOption[] = [
      { label: trans('showtimes.today'), value: today },
      {
        label: trans('showtimes.tomorrow'),
        value: resetTimeForDate(addDay(today, 1))
      }
    ];
    for (let dayOffset = 2; dayOffset <= DAYS_AHEAD; dayOffset += 1) {
      const value = resetTimeForDate(addDay(today, dayOffset));
      options.push({ label: capitalize(formatDate(value, DAY_FORMAT)), value });
    }
    return options;
  }, []);

  // Fetch showtimes for a favorite theater on a given day (cached per theater/day)
  const loadShowtimes = useCallback(
    async (theaterId: string, date: Date) => {
      const dayFormatted = formatDayString(date);
      const cacheKey = `${theaterId}_${dayFormatted}`;

      if (!movie || theaterDataMap[cacheKey]) {
        return;
      }

      setLoading(true);

      try {
        const response = await requestShowtimes<
          RequestFavoriteData,
          RequestPayload
        >(
          'internal_api_showtimes_my_theaters_filtered',
          {
            movie: movie.internalId,
            day: dayFormatted
          },
          {
            theaters: [theaterId]
          }
        );

        const theaterNode = response.results?.edges?.[0]?.node;

        setTheaterDataMap(prev => ({
          ...prev,
          [cacheKey]: theaterNode
            ? {
                theater: theaterNode.theater || null,
                showtimes: buildShowtimesGroups(theaterNode)
              }
            : { theater: null, showtimes: null, message: response.message }
        }));
      } catch {
        // non-blocking enhancement: on failure we simply show no card
      } finally {
        setLoading(false);
      }
    },
    [movie, theaterDataMap]
  );

  // Load showtimes when the selected theater or day changes
  React.useEffect(() => {
    if (selectedTheaterId && movie) {
      loadShowtimes(selectedTheaterId, selectedDate);
    }
  }, [selectedTheaterId, selectedDate, movie, loadShowtimes]);

  if (!movie || !favoriteTheaters.length) {
    return null;
  }

  const cacheKey = selectedTheaterId
    ? `${selectedTheaterId}_${formatDayString(selectedDate)}`
    : undefined;
  const selectedTheaterData = cacheKey ? theaterDataMap[cacheKey] : undefined;
  const { theater, showtimes } = selectedTheaterData || {};
  const showCard = !!theater && hasAnyShowtime(showtimes);
  const showEmpty = !loading && !!selectedTheaterData && !showCard;

  const trackingObj: Omit<TheaterReservationTracking, 'showtime'> = {
    movie,
    theater
  };

  return (
    <div className="moviepage-favorite-cinemas">
      <div className="moviepage-favorite-cinemas-filters">
        {favoriteTheaters.length > 1 && (
          <Dropdown
            className="moviepage-favorite-cinemas-dropdown"
            onChange={setSelectedTheaterId}
            value={selectedTheaterId}
            valueRenderer={(value: string) =>
              favoriteTheaters.find(t => t.id === value)?.label || ''
            }
          >
            {callback =>
              favoriteTheaters.map(favoriteTheater => (
                <li
                  onClick={callback.bind(null, favoriteTheater.id)}
                  className="item"
                  key={favoriteTheater.id}
                >
                  <span className="item-content">{favoriteTheater.label}</span>
                </li>
              ))
            }
          </Dropdown>
        )}

        <Dropdown
          className="moviepage-favorite-cinemas-dropdown"
          onChange={setSelectedDate}
          value={selectedDate}
          valueRenderer={(value: Date) =>
            dateOptions.find(option => isSameDay(option.value, value))?.label ||
            ''
          }
        >
          {callback =>
            dateOptions.map(option => (
              <li
                onClick={callback.bind(null, option.value)}
                className="item"
                key={option.label}
              >
                <span className="item-content">{option.label}</span>
              </li>
            ))
          }
        </Dropdown>
      </div>

      {showCard && showtimes && (
        <div className="moviepage-favorite-cinema-card">
          <TheaterCard theater={theater} user={user} dispatch={dispatch}>
            <ShowtimesVersionHolder
              currentDay={formatDayString(selectedDate)}
              openModal={(showtimeId, fallback, trackingContext) =>
                openShowtimeModal({
                  fallback,
                  showtimeId,
                  trackingContext
                })
              }
              showtimes={showtimes}
              theaterHasBooking={theater?.flags?.hasBooking}
              tracking={trackingObj}
              versionHint={
                <div className="moviepage-favorite-cinema-card-hint">
                  {trans('moviepage_showtimes.e-ticket.text')}
                </div>
              }
            />
          </TheaterCard>
        </div>
      )}

      {showEmpty && (
        <div className="alert alert-warning">
          <div className="alert-content">
            <span>{trans('no.showtime.favorites.warning.title')}</span>
            <div>{trans('no.showtime.favorites.warning.subtitle')}</div>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: State) => ({
  user: state.user
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  dispatch
});

const ConnectedComponent = connect(
  mapStateToProps,
  mapDispatchToProps
)(FavoriteCinemasShowtimeUi);

export default function moviepageFavoriteCinemasShowtimes() {
  const anchors = document.getElementsByClassName(
    'js-favorite-cinemas-showtimes'
  );

  if (!anchors.length) {
    return;
  }

  for (const anchor of anchors) {
    const movieTitle = (anchor as HTMLElement).getAttribute('data-movie-title');
    const movieInternalId = (anchor as HTMLElement).getAttribute(
      'data-movie-internal-id'
    );

    if (movieInternalId) {
      const movie: MovieFromAttributes = {
        internalId: Number(movieInternalId),
        title: movieTitle || ''
      };

      mountComponent(anchor as HTMLElement, movie);
    }
  }
}

async function mountComponent(
  anchorElement: HTMLElement,
  movie: MovieFromAttributes
) {
  // Only mount for logged-in users
  const user = await waitForLoginStatus();

  if (!user.loggedIn) {
    return;
  }

  // Get the user's favorite theaters
  const { retrieveUserTheaters } = await import('common/api/SecureApi');

  try {
    const theaters = await retrieveUserTheaters();

    if (!theaters.targets) {
      return;
    }

    const favoriteTheaters: FavoriteTheater[] = [];
    Object.entries(theaters.targets).forEach(([key, value]: [string, any]) => {
      if (key.indexOf('theater') !== -1) {
        favoriteTheaters.push({
          id: value.id,
          label: value.label
        });
      }
    });

    if (!favoriteTheaters.length) {
      return;
    }

    // Integrate the favorite cinemas block into the original showtimes section
    const showtimesSection = document.querySelector(
      '.js-favorite-cinemas-showtimes ~ section.section.ovw'
    ) as HTMLElement;

    if (!showtimesSection) {
      return;
    }

    // Remove the search form and the "more cities" list, no longer relevant
    showtimesSection.querySelector('.ovw-search')?.remove();
    showtimesSection.querySelector('.mdl-more.gd.small-crop')?.remove();

    // Change the subtitle text based on number of favorite cinemas
    const { default: trans } = await import('common/tools/translations/trans');
    const subtitle = showtimesSection.querySelector('.titlebar-title-xs');
    if (subtitle) {
      const translationKey =
        favoriteTheaters.length === 1
          ? 'showtimes.in-favorite-theater'
          : 'showtimes.in-favorites-theaters';
      subtitle.textContent = trans(translationKey);
    }

    // Mount the block just before the end-section button, after the titlebars
    const endSection = showtimesSection.querySelector(
      '.end-section-link-container'
    );
    if (endSection) {
      endSection.before(anchorElement);
    } else {
      showtimesSection.append(anchorElement);
    }

    // Get the Redux store
    const store = await import('website/store').then(m => m.default);

    const root = createRoot(anchorElement);
    const { Provider } = await import('react-redux');

    root.render(
      <Provider store={store}>
        <ConnectedComponent movie={movie} favoriteTheaters={favoriteTheaters} />
      </Provider>
    );
  } catch {
    // non-blocking enhancement: on failure we keep the default showtimes section
  }
}
